<template>
  <div class="badgeBasicForm">
    <!-- 等级 / 积分横排：步进器 + 旁侧提示/快选，不撑满整行留白 -->
    <div class="badgeBasicForm__row">
      <el-form-item label="等级" required data-field-key="badgeLevel" class="badgeBasicForm__item">
        <div class="badgeBasicForm__inline">
          <workout-number-stepper
            v-model="local.badgeLevel"
            :min="0"
            :max="BADGE_LEVEL_MAX"
            :step="1"
            :precision="0"
            aria-label="等级"
          />
          <span class="badgeBasicForm__hint">最大 {{ BADGE_LEVEL_MAX }}</span>
        </div>
      </el-form-item>

      <el-form-item label="积分" required data-field-key="badgePoints" class="badgeBasicForm__item">
        <div class="badgeBasicForm__inline">
          <workout-number-stepper
            v-model="local.badgePoints"
            :min="0"
            :step="1"
            :precision="0"
            aria-label="积分"
          />
          <div class="badgeBasicForm__quick" role="group" aria-label="积分快选">
            <span class="badgeBasicForm__hint">快选常用积分</span>
            <div class="badgeBasicForm__chips">
              <button
                v-for="preset in BADGE_POINTS_PRESETS"
                :key="preset"
                type="button"
                class="badgeBasicForm__chip"
                :class="{ 'badgeBasicForm__chip--active': local.badgePoints === preset }"
                @click="selectPointsPreset(preset)"
              >
                {{ preset }}
              </button>
            </div>
          </div>
        </div>
      </el-form-item>
    </div>

    <el-form-item label="徽章描述" required>
      <el-input
        v-model="local.badgeDescription"
        type="textarea"
        :rows="3"
        placeholder="描述徽章获得方式或含义，例如 Complete 10,000 steps in one day"
      />
    </el-form-item>
  </div>
</template>

<script>
import basicFormMixin from './basicFormMixin'
import WorkoutNumberStepper from './WorkoutNumberStepper.vue'
import { parseNonNegativeInt } from './badgeIntegerFields'

/** 徽章等级上限：与步进器 max 一致，保存时再挡一手非法值 */
const BADGE_LEVEL_MAX = 6

/** 积分快选预设：运营常用奖励档位 */
const BADGE_POINTS_PRESETS = [10, 20, 30, 40, 50, 100]

const EMPTY = () => ({
  badgeDescription: '',
  badgeLevel: null,
  badgePoints: null,
})

function parseBadgeLevel(value) {
  const num = parseNonNegativeInt(value)
  if (num == null || num > BADGE_LEVEL_MAX) {
    return null
  }
  return num
}

export default {
  name: 'BadgeBasicForm',
  components: { WorkoutNumberStepper },
  mixins: [basicFormMixin],
  data() {
    return {
      local: EMPTY(),
      BADGE_LEVEL_MAX,
      BADGE_POINTS_PRESETS,
    }
  },
  methods: {
    selectPointsPreset(preset) {
      this.local.badgePoints = preset
    },
    /**
     * badgeLevel / badgePoints 与 badgeDescription 同属 jsonConfig.exta：
     * 等级决定奖励积分；App 读整包 exta，CMS 不做积分发放逻辑。
     */
    buildExtaPayload() {
      return {
        badgeDescription: String(this.local.badgeDescription || '').trim(),
        badgeLevel: parseBadgeLevel(this.local.badgeLevel),
        badgePoints: parseNonNegativeInt(this.local.badgePoints),
      }
    },
    loadFromExta(exta) {
      const data = exta || {}
      const level = parseBadgeLevel(data.badgeLevel)
      const points = parseNonNegativeInt(data.badgePoints)
      this.local = {
        badgeDescription: data.badgeDescription || '',
        badgeLevel: level,
        badgePoints: points,
      }
    },
    reset() {
      this.local = EMPTY()
    },
    validate() {
      if (parseBadgeLevel(this.local.badgeLevel) == null) {
        return Promise.reject(`请填写徽章等级（0～${BADGE_LEVEL_MAX} 的整数）`)
      }
      if (parseNonNegativeInt(this.local.badgePoints) == null) {
        return Promise.reject('请填写奖励积分（非负整数）')
      }
      if (!String(this.local.badgeDescription || '').trim()) {
        return Promise.reject('请填写徽章描述')
      }
      return Promise.resolve()
    },
    captureSnapshot() {
      return { ...this.local }
    },
  },
}
</script>

<style scoped>
.badgeBasicForm__row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 8px 40px;
  margin-bottom: 4px;
}

/* 内容宽度随控件走，不 flex:1 撑满，避免右边大片空白 */
.badgeBasicForm__item {
  flex: 0 1 auto;
  margin-bottom: 18px;
}

.badgeBasicForm__inline {
  display: flex;
  align-items: center;
  gap: 10px 12px;
  flex-wrap: wrap;
}

.badgeBasicForm__hint {
  color: var(--link-ink-muted, #8a837a);
  font-size: 13px;
  line-height: 1.4;
  white-space: nowrap;
}

.badgeBasicForm__quick {
  display: flex;
  align-items: center;
  gap: 8px 10px;
  flex-wrap: wrap;
}

.badgeBasicForm__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.badgeBasicForm__chip {
  margin: 0;
  padding: 4px 10px;
  border: 1px solid var(--link-line-strong, #d6d3d1);
  border-radius: 999px;
  background: var(--link-surface, #fff);
  color: var(--link-ink-secondary, #4a4541);
  font-size: 13px;
  line-height: 1.3;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.badgeBasicForm__chip:hover {
  border-color: var(--link-accent, #a67c52);
  color: var(--link-accent, #a67c52);
  background: var(--link-accent-bg, #f5efe3);
}

.badgeBasicForm__chip--active {
  border-color: var(--link-accent, #a67c52);
  background: var(--link-accent-bg, #f5efe3);
  color: var(--link-accent, #a67c52);
  font-weight: 600;
}
</style>
